import {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  useContext,
  lazy,
  Suspense,
} from 'react';
import { T, Var, useGT } from 'gt-react';
import {
  WorkflowExecutionDurationPattern,
  WorkflowExecutionDurationSchema,
  type WorkflowStepExecutionOverride,
} from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { ScrollArea } from 'src/components/ui/scroll-area';
import { Badge } from 'src/components/ui/badge';
import { Separator } from 'src/components/ui/separator';
import { Skeleton } from 'src/components/ui/skeleton';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'src/components/ui/tabs';
import {
  ArrowsClockwiseIcon,
  CheckIcon,
  CopyIcon,
  DownloadSimpleIcon,
  FolderOpenIcon,
  HammerIcon,
  InfoIcon,
  MarkdownLogoIcon,
  PencilSimpleIcon,
  PlusIcon,
  TextboxIcon,
  UploadSimpleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Switch } from 'src/components/ui/switch';
import { Textarea } from 'src/components/ui/textarea';
import { MarkdownWYSIWYGEditor } from 'src/components/common/MarkdownWYSIWYGEditor';
import { looksLikeMarkdown } from './looksLikeMarkdown';
import { cn } from 'src/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { getCategoryStyle } from 'src/lib/workflows/categoryStyles';
import { getCategoryIcon } from 'src/lib/workflows/categoryIcons';
import { DYNAMIC_SOCKET_BEHAVIORS } from 'src/lib/workflows/editorUtils';
import {
  FORM_TRIGGER_STEP_TYPE,
  HTTP_TRIGGER_STEP_TYPE,
  getWorkflowGuardrailAssignmentConfigs,
} from '@erp/workflows';
import { NodeLibraryContent } from './NodeLibraryContent';
import {
  getConfigRenderer,
  type StepNode,
  type RegisterStepTool,
} from './config-renderers';
import { GuardrailPolicyAssignments } from './config-renderers/GuardrailPolicyAssignments';
import { BatchOptionsFields } from './config-renderers/BatchOptionsFields';
// Behind the "tests" tab, and the largest thing the panel can reach. Loading it
// with the panel put the artifact + document viewers on every step click.
const loadTestSuiteTab = () => import('./test-suites/TestSuiteTab');
const TestSuiteTab = lazy(async () => ({
  default: (await loadTestSuiteTab()).TestSuiteTab,
}));
import { useListTestSuites } from 'src/api/rpc/hooks/test-suites/useTestSuites';
import { useToast } from 'src/context/ToastContext';
import { StepTestSection, hasDocumentInputSockets } from './StepTestSection';
import { GenericStepTestSection } from './GenericStepTestSection';
import { StepConfigAssistant } from './StepConfigAssistant';
import type { WorkflowVersionViewVariant } from './versionState';
import {
  PanelSectionCollapsible,
  PanelSectionBadge,
} from './PanelSectionCollapsible';
import { useWorkflowPanelSections } from '../hooks/useWorkflowPanelSections';
import { useStepTypeConfigSchema } from '@/api/rpc/hooks/workflows/useWorkflowDefinitions';
import { AuthContext } from '@/context/auth.context';
import { warmChunk } from 'src/lib/warmChunk';

/**
 * Step types whose `Test` tab uses the generic per-input form (no document
 * upload). Add new step types here to surface the Test tab without forcing
 * them through the document-driven path.
 */
const GENERIC_TEST_TAB_STEP_TYPES: ReadonlySet<string> = new Set([
  'external_http_call',
]);

const GUARDED_STEP_TYPES: ReadonlySet<string> = new Set([
  'background_agent',
  'classify_email',
  'classify_document_v2',
  'extract_document_content_v2',
]);

interface WorkflowEditorPanelProps {
  selectedNode: StepNode | null;
  onConfigChange: (
    nodeId: string,
    newConfig: Record<string, unknown>,
  ) => void | Promise<unknown>;
  onLabelChange: (nodeId: string, newLabel: string) => void;
  onClose: () => void;
  onExpand: () => void;
  isMinimized: boolean;
  workflowDefinitionId?: string;
  readOnly?: boolean;
  readOnlyVariant?: WorkflowVersionViewVariant;
  /** Read-only step inspection (e.g. the Steps tab) — shorter copy, no edit hints. */
  viewOnly?: boolean;
  /** Register awaitable dynamic-config tools for Nami (bubbled to the editor). */
  registerStepTool?: RegisterStepTool;
}

function ReadOnlyPanelPlaceholder({
  variant,
  viewOnly,
}: {
  variant?: WorkflowVersionViewVariant;
  viewOnly?: boolean;
}) {
  if (variant === 'historical') {
    return viewOnly ? (
      <T context="workflow editor side panel empty state when viewing an older published version">
        Viewing a historical version. Select a node to inspect its
        configuration.
      </T>
    ) : (
      <T context="workflow editor side panel empty state when viewing an older published version">
        Viewing a historical version. Select a node to inspect its
        configuration. Create a new draft from the header to make changes.
      </T>
    );
  }

  if (variant === 'unpublished') {
    return viewOnly ? (
      <T context="workflow editor side panel empty state when viewing a finalized but unpublished version">
        Viewing a finalized version that is not published yet. Select a node to
        inspect its configuration.
      </T>
    ) : (
      <T context="workflow editor side panel empty state when viewing a finalized but unpublished version">
        Viewing a finalized version that is not published yet. Select a node to
        inspect its configuration. Publish from the header or create a draft to
        make changes.
      </T>
    );
  }

  if (variant === 'other-draft') {
    return (
      <T context="workflow editor side panel empty state when viewing another user's draft">
        Viewing another user&apos;s draft. Select a node to inspect its
        configuration.
      </T>
    );
  }

  return viewOnly ? (
    <T context="workflow editor side panel empty state when viewing the live published version">
      Select a node to inspect its configuration.
    </T>
  ) : (
    <T context="workflow editor side panel empty state when viewing the live published version">
      Select a node to inspect its configuration. Create a new draft from the
      header to make changes.
    </T>
  );
}

type SocketSchemaEntry = {
  id: string;
  name: string;
  type: string;
  required?: boolean;
  description?: string;
};

type StepTypeMetadata = {
  inputSockets?: SocketSchemaEntry[];
  outputSockets?: SocketSchemaEntry[];
};

type ExecutionOverrideConfig = Pick<
  WorkflowStepExecutionOverride,
  | 'startToCloseTimeout'
  | 'scheduleToCloseTimeout'
  | 'heartbeatTimeout'
  | 'retry'
>;

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function httpTriggerBodySchemaPinned(
  config: Record<string, unknown> | undefined,
): boolean {
  if (!config) return false;
  return (
    isNonEmptyString(config.bodySchemaDefinitionId) &&
    isNonEmptyString(config.bodySchemaDefinitionVersionId)
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getTabGridCols(
  showEvalsTab: boolean,
  showDocTestTab: boolean,
): string {
  const n = 1 + (showDocTestTab ? 1 : 0) + (showEvalsTab ? 1 : 0);
  if (n <= 1) return 'grid-cols-1';
  if (n === 2) return 'grid-cols-2';
  return 'grid-cols-3';
}

function countStaticInputs(config: Record<string, unknown>): number {
  const staticInputs = config['__static_inputs'];
  if (!isRecord(staticInputs)) return 0;
  return Object.keys(staticInputs).length;
}

function isNonEmptyTrimmedString(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function hasExecutionOverrides(config: Record<string, unknown>): boolean {
  const executionValue = config.execution;
  if (!isRecord(executionValue)) return false;
  if (
    isNonEmptyTrimmedString(executionValue.startToCloseTimeout) ||
    isNonEmptyTrimmedString(executionValue.scheduleToCloseTimeout) ||
    isNonEmptyTrimmedString(executionValue.heartbeatTimeout)
  ) {
    return true;
  }
  const retry = executionValue.retry;
  if (isRecord(retry)) {
    if (
      retry.maximumAttempts !== undefined ||
      retry.backoffCoefficient !== undefined ||
      isNonEmptyTrimmedString(retry.initialInterval) ||
      isNonEmptyTrimmedString(retry.maximumInterval)
    ) {
      return true;
    }
  }
  return false;
}

function getExecutionOverride(
  config: Record<string, unknown>,
): ExecutionOverrideConfig {
  const executionValue = config.execution;
  if (!isRecord(executionValue)) {
    return {};
  }

  const execution = executionValue;
  const retryValue = execution.retry;
  const retry = isRecord(retryValue) ? retryValue : undefined;

  return {
    startToCloseTimeout:
      typeof execution.startToCloseTimeout === 'string'
        ? execution.startToCloseTimeout
        : undefined,
    scheduleToCloseTimeout:
      typeof execution.scheduleToCloseTimeout === 'string'
        ? execution.scheduleToCloseTimeout
        : undefined,
    heartbeatTimeout:
      typeof execution.heartbeatTimeout === 'string'
        ? execution.heartbeatTimeout
        : undefined,
    retry: retry
      ? {
          maximumAttempts:
            typeof retry.maximumAttempts === 'number'
              ? retry.maximumAttempts
              : undefined,
          backoffCoefficient:
            typeof retry.backoffCoefficient === 'number'
              ? retry.backoffCoefficient
              : undefined,
          initialInterval:
            typeof retry.initialInterval === 'string'
              ? retry.initialInterval
              : undefined,
          maximumInterval:
            typeof retry.maximumInterval === 'string'
              ? retry.maximumInterval
              : undefined,
        }
      : undefined,
  };
}

function withExecutionOverride(
  config: Record<string, unknown>,
  execution: ExecutionOverrideConfig,
): Record<string, unknown> {
  const existingExecution = isRecord(config.execution)
    ? { ...config.execution }
    : {};

  const durationFields = [
    'startToCloseTimeout',
    'scheduleToCloseTimeout',
    'heartbeatTimeout',
  ] as const;
  for (const field of durationFields) {
    const value = execution[field];
    if (value === undefined) continue;
    const trimmed = value.trim();
    if (trimmed) {
      existingExecution[field] = trimmed;
    } else {
      delete existingExecution[field];
    }
  }

  const existingRetry = isRecord(existingExecution.retry)
    ? { ...existingExecution.retry }
    : {};
  if (execution.retry !== undefined) {
    const numericRetryFields = [
      'maximumAttempts',
      'backoffCoefficient',
    ] as const;
    for (const field of numericRetryFields) {
      const value = execution.retry[field];
      if (value !== undefined && Number.isFinite(value)) {
        existingRetry[field] = value;
      } else {
        delete existingRetry[field];
      }
    }

    const intervalFields = ['initialInterval', 'maximumInterval'] as const;
    for (const field of intervalFields) {
      const value = execution.retry[field];
      const trimmed = value?.trim();
      if (trimmed) {
        existingRetry[field] = trimmed;
      } else {
        delete existingRetry[field];
      }
    }
  }

  if (Object.keys(existingRetry).length > 0) {
    existingExecution.retry = existingRetry;
  } else {
    delete existingExecution.retry;
  }

  const { execution: _execution, ...rest } = config;
  if (Object.keys(existingExecution).length === 0) {
    return rest;
  }

  return {
    ...rest,
    execution: existingExecution,
  };
}

type DurationOverrideField =
  'startToCloseTimeout' | 'scheduleToCloseTimeout' | 'heartbeatTimeout';

type RetryIntervalField = 'initialInterval' | 'maximumInterval';

const MAXIMUM_ATTEMPT_OPTIONS = [
  { value: '1', label: '1 - No retries' },
  { value: '2', label: '2 - Retry once' },
  { value: '3', label: '3 - Retry twice' },
  { value: '5', label: '5 - Retry up to 4 times' },
  { value: '0', label: '0 - Unlimited retries' },
] as const;

function getDurationOverrideError(value: string): string | undefined {
  const trimmedValue = value.trim();
  if (trimmedValue.length === 0) {
    return undefined;
  }

  const parsedDuration =
    WorkflowExecutionDurationSchema.safeParse(trimmedValue);
  if (!parsedDuration.success) {
    return parsedDuration.error.issues[0]?.message;
  }

  return undefined;
}

function TimeoutLabel({
  htmlFor,
  label,
  description,
}: {
  htmlFor: string;
  label: string;
  description: string;
}) {
  const gt = useGT();
  return (
    <div className="flex items-center gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center text-muted-foreground hover:text-foreground"
            aria-label={gt('{label} info', { label })}
          >
            <InfoIcon className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-64 text-xs">
          {description}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

function normalizeDurationOverride(value: string): string | undefined {
  const trimmedValue = value.trim();
  if (trimmedValue.length === 0) {
    return undefined;
  }

  const match = trimmedValue.match(WorkflowExecutionDurationPattern);
  if (!match) {
    return undefined;
  }

  const amount = Number(match[1]);
  const unit = match[2]?.toLowerCase() ?? 'minutes';

  let normalizedUnit = 'minute';
  if (unit.startsWith('sec')) {
    normalizedUnit = 'second';
  } else if (unit.startsWith('hr') || unit.startsWith('hour')) {
    normalizedUnit = 'hour';
  }

  return `${amount} ${amount === 1 ? normalizedUnit : `${normalizedUnit}s`}`;
}

function DurationOverrideInput({
  id,
  label,
  description,
  placeholder,
  value,
  onChange,
  onCommit,
}: {
  id: string;
  label: string;
  description: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onCommit: () => void;
}) {
  const error = getDurationOverrideError(value);
  return (
    <div className="space-y-2">
      <TimeoutLabel htmlFor={id} label={label} description={description} />
      <Input
        id={id}
        placeholder={placeholder}
        value={value}
        aria-invalid={error ? 'true' : 'false'}
        className={cn(error && 'border-destructive')}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        onBlur={onCommit}
      />
      <p
        className={cn(
          'text-xs',
          error ? 'text-destructive' : 'text-muted-foreground',
        )}
      >
        {error ?? (
          <T>
            Allowed: seconds, minutes, hours. Bare numbers default to minutes.
          </T>
        )}
      </p>
    </div>
  );
}

function ExecutionConfigSection({
  config,
  onConfigChange,
  readOnly = false,
}: {
  config: Record<string, unknown>;
  onConfigChange: (newConfig: Record<string, unknown>) => void;
  readOnly?: boolean;
}) {
  const gt = useGT();
  const execution = getExecutionOverride(config);

  const savedStartToClose = execution.startToCloseTimeout ?? '';
  const savedScheduleToClose = execution.scheduleToCloseTimeout ?? '';
  const savedHeartbeat = execution.heartbeatTimeout ?? '';
  const savedInitialInterval = execution.retry?.initialInterval ?? '';
  const savedMaximumInterval = execution.retry?.maximumInterval ?? '';
  const savedBackoff =
    execution.retry?.backoffCoefficient !== undefined
      ? String(execution.retry.backoffCoefficient)
      : '';

  const [durationInputs, setDurationInputs] = useState({
    startToCloseTimeout: savedStartToClose,
    scheduleToCloseTimeout: savedScheduleToClose,
    heartbeatTimeout: savedHeartbeat,
    initialInterval: savedInitialInterval,
    maximumInterval: savedMaximumInterval,
  });
  const [backoffInput, setBackoffInput] = useState(savedBackoff);

  // Re-sync local inputs whenever the saved override changes (e.g. switching
  // steps or after a value is normalized on commit).
  useEffect(() => {
    setDurationInputs({
      startToCloseTimeout: savedStartToClose,
      scheduleToCloseTimeout: savedScheduleToClose,
      heartbeatTimeout: savedHeartbeat,
      initialInterval: savedInitialInterval,
      maximumInterval: savedMaximumInterval,
    });
    setBackoffInput(savedBackoff);
  }, [
    savedStartToClose,
    savedScheduleToClose,
    savedHeartbeat,
    savedInitialInterval,
    savedMaximumInterval,
    savedBackoff,
  ]);

  const setDurationInput = useCallback(
    (field: DurationOverrideField | RetryIntervalField, value: string) => {
      setDurationInputs((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const commitTopLevelDuration = useCallback(
    (field: DurationOverrideField, rawValue: string) => {
      if (getDurationOverrideError(rawValue)) {
        return;
      }
      const normalizedValue = normalizeDurationOverride(rawValue);
      setDurationInput(field, normalizedValue ?? '');
      onConfigChange(
        withExecutionOverride(config, {
          ...execution,
          [field]: normalizedValue,
        }),
      );
    },
    [config, execution, onConfigChange, setDurationInput],
  );

  const commitRetryInterval = useCallback(
    (field: RetryIntervalField, rawValue: string) => {
      if (getDurationOverrideError(rawValue)) {
        return;
      }
      const normalizedValue = normalizeDurationOverride(rawValue);
      setDurationInput(field, normalizedValue ?? '');
      onConfigChange(
        withExecutionOverride(config, {
          ...execution,
          retry: { ...execution.retry, [field]: normalizedValue },
        }),
      );
    },
    [config, execution, onConfigChange, setDurationInput],
  );

  const backoffError =
    backoffInput.trim().length > 0 &&
    !(Number.isFinite(Number(backoffInput)) && Number(backoffInput) >= 1)
      ? gt('Enter a number greater than or equal to 1.')
      : undefined;

  const commitBackoffCoefficient = useCallback(
    (rawValue: string) => {
      const trimmed = rawValue.trim();
      let backoffCoefficient: number | undefined;
      if (trimmed.length > 0) {
        const parsed = Number(trimmed);
        if (!Number.isFinite(parsed) || parsed < 1) {
          return;
        }
        backoffCoefficient = parsed;
      }
      setBackoffInput(
        backoffCoefficient !== undefined ? String(backoffCoefficient) : '',
      );
      onConfigChange(
        withExecutionOverride(config, {
          ...execution,
          retry: { ...execution.retry, backoffCoefficient },
        }),
      );
    },
    [config, execution, onConfigChange],
  );

  return (
    <fieldset
      disabled={readOnly}
      className="space-y-3 rounded-md border p-3 disabled:opacity-60"
    >
      <div className="space-y-1">
        <h4 className="text-sm font-medium">
          <T>Execution</T>
        </h4>
        <p className="text-xs text-muted-foreground">
          <T>
            Optional per-step Temporal overrides stored in the workflow
            definition. Allowed units are seconds, minutes, and hours. If you
            omit the unit, the value defaults to minutes, so{' '}
            <Var>
              <code>60</code>
            </Var>{' '}
            becomes{' '}
            <Var>
              <code>60 minutes</code>
            </Var>
            .
          </T>
        </p>
      </div>

      <DurationOverrideInput
        id="start-to-close-timeout"
        label={gt('Start To Close Timeout')}
        description={gt(
          'Maximum runtime for one attempt after a worker starts the step. This limit applies separately to each try and does not include retry delays or queue time.',
        )}
        placeholder={gt('e.g. 60, 60 min, 90 sec, 1 hr', {
          context: 'placeholder for start-to-close timeout duration input',
        })}
        value={durationInputs.startToCloseTimeout}
        onChange={(value) => {
          setDurationInput('startToCloseTimeout', value);
        }}
        onCommit={() => {
          commitTopLevelDuration(
            'startToCloseTimeout',
            durationInputs.startToCloseTimeout,
          );
        }}
      />

      <DurationOverrideInput
        id="schedule-to-close-timeout"
        label={gt('Schedule To Close Timeout')}
        description={gt(
          'Maximum total time for the entire step from scheduling to completion. This includes queue time, retry delays, and every retry attempt combined.',
        )}
        placeholder={gt('e.g. 120, 120 min, 2 hr', {
          context: 'placeholder for schedule-to-close timeout duration input',
        })}
        value={durationInputs.scheduleToCloseTimeout}
        onChange={(value) => {
          setDurationInput('scheduleToCloseTimeout', value);
        }}
        onCommit={() => {
          commitTopLevelDuration(
            'scheduleToCloseTimeout',
            durationInputs.scheduleToCloseTimeout,
          );
        }}
      />

      <DurationOverrideInput
        id="heartbeat-timeout"
        label={gt('Heartbeat Timeout')}
        description={gt(
          'Maximum time allowed between heartbeats while the step runs. Long-running steps auto-heartbeat every 30 seconds; raise this only if a step legitimately goes silent for longer.',
        )}
        placeholder={gt('e.g. 120, 2 min', {
          context: 'placeholder for heartbeat timeout duration input',
        })}
        value={durationInputs.heartbeatTimeout}
        onChange={(value) => {
          setDurationInput('heartbeatTimeout', value);
        }}
        onCommit={() => {
          commitTopLevelDuration(
            'heartbeatTimeout',
            durationInputs.heartbeatTimeout,
          );
        }}
      />

      <div className="space-y-2">
        <Label htmlFor="maximum-attempts">
          <T>Maximum Attempts</T>
        </Label>
        <Select
          value={execution.retry?.maximumAttempts?.toString() ?? '__default__'}
          onValueChange={(value) => {
            onConfigChange(
              withExecutionOverride(config, {
                ...execution,
                retry: {
                  ...execution.retry,
                  maximumAttempts:
                    value === '__default__' ? undefined : Number(value),
                },
              }),
            );
          }}
        >
          <SelectTrigger id="maximum-attempts">
            <SelectValue placeholder={gt('Use the step default')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__default__">
              <T>Use the step default</T>
            </SelectItem>
            {execution.retry?.maximumAttempts !== undefined &&
              !MAXIMUM_ATTEMPT_OPTIONS.some(
                (option) =>
                  option.value === execution.retry?.maximumAttempts?.toString(),
              ) && (
                <SelectItem value={execution.retry.maximumAttempts.toString()}>
                  <T>
                    <Var>{execution.retry.maximumAttempts}</Var> - Custom saved
                    value
                  </T>
                </SelectItem>
              )}
            {MAXIMUM_ATTEMPT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <Var>{option.label}</Var>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          <T>
            Total attempts, including the first run. Based on Temporal retry
            policy semantics,{' '}
            <Var>
              <code>1</code>
            </Var>{' '}
            means no retries and{' '}
            <Var>
              <code>0</code>
            </Var>{' '}
            means unlimited retries until the step succeeds or hits its timeout.
          </T>
        </p>
      </div>

      <DurationOverrideInput
        id="retry-initial-interval"
        label={gt('Initial Retry Interval')}
        description={gt(
          'How long to wait before the first retry. Each subsequent wait is multiplied by the backoff coefficient.',
        )}
        placeholder={gt('e.g. 30 sec, 1 min', {
          context: 'placeholder for initial retry interval duration input',
        })}
        value={durationInputs.initialInterval}
        onChange={(value) => {
          setDurationInput('initialInterval', value);
        }}
        onCommit={() => {
          commitRetryInterval(
            'initialInterval',
            durationInputs.initialInterval,
          );
        }}
      />

      <div className="space-y-2">
        <TimeoutLabel
          htmlFor="retry-backoff-coefficient"
          label={gt('Backoff Coefficient')}
          description={gt(
            'Multiplier applied to the retry interval after each attempt. A value of 2 doubles the wait each time (1m, then 2m, then 4m). Must be 1 or greater.',
          )}
        />
        <Input
          id="retry-backoff-coefficient"
          type="number"
          min={1}
          step="any"
          placeholder={gt('e.g. 2', {
            context: 'placeholder for retry backoff coefficient input',
          })}
          value={backoffInput}
          aria-invalid={backoffError ? 'true' : 'false'}
          className={cn(backoffError && 'border-destructive')}
          onChange={(e) => {
            setBackoffInput(e.target.value);
          }}
          onBlur={() => {
            commitBackoffCoefficient(backoffInput);
          }}
        />
        <p
          className={cn(
            'text-xs',
            backoffError ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {backoffError ?? (
            <T>Leave blank to use the step default. Must be 1 or greater.</T>
          )}
        </p>
      </div>

      <DurationOverrideInput
        id="retry-maximum-interval"
        label={gt('Maximum Retry Interval')}
        description={gt(
          'Upper bound on the wait between retries. The interval grows by the backoff coefficient but never exceeds this value.',
        )}
        placeholder={gt('e.g. 4 min, 5 min', {
          context: 'placeholder for maximum retry interval duration input',
        })}
        value={durationInputs.maximumInterval}
        onChange={(value) => {
          setDurationInput('maximumInterval', value);
        }}
        onCommit={() => {
          commitRetryInterval(
            'maximumInterval',
            durationInputs.maximumInterval,
          );
        }}
      />
    </fieldset>
  );
}

function parseSocketDescription(description?: string): {
  text?: string;
  shapeLabel?: string;
  shape?: string;
} {
  if (!description) {
    return {};
  }

  const itemShapeMatch = description.match(
    /^(.*?)(?:\.\s*)?Item shape:\s*(\{.*\})$/,
  );
  if (itemShapeMatch) {
    const text = itemShapeMatch[1].trim();
    return {
      text: text.length > 0 ? text : undefined,
      shapeLabel: 'Item shape',
      shape: itemShapeMatch[2],
    };
  }

  const shapeMatch = description.match(/^(.*?)(?:\.\s*)?Shape:\s*(\{.*\})$/);
  if (shapeMatch) {
    const text = shapeMatch[1].trim();
    return {
      text: text.length > 0 ? text : undefined,
      shapeLabel: 'Shape',
      shape: shapeMatch[2],
    };
  }

  return { text: description };
}

function SocketList({
  entries,
  keyPrefix,
  emptyLabel,
  showRequired,
}: {
  entries: SocketSchemaEntry[];
  keyPrefix: string;
  emptyLabel: string;
  showRequired?: boolean;
}) {
  if (entries.length === 0) {
    return <p className="text-xs text-muted-foreground">{emptyLabel}</p>;
  }

  return (
    <>
      {entries.map((socket) => {
        const parsedDescription = parseSocketDescription(socket.description);

        return (
          <div
            key={`${keyPrefix}-${socket.id}`}
            className="rounded-md border p-2 space-y-1.5"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium">{socket.name}</span>
              <Badge variant="outline" className="text-[10px]">
                {socket.type}
              </Badge>
              {showRequired && (
                <Badge variant="secondary" className="text-[10px]">
                  {socket.required === false ? (
                    <T>Optional</T>
                  ) : (
                    <T>Required</T>
                  )}
                </Badge>
              )}
            </div>
            {parsedDescription.text && (
              <p className="text-xs text-muted-foreground">
                {parsedDescription.text}
              </p>
            )}
            {parsedDescription.shape && (
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  {parsedDescription.shapeLabel}
                </p>
                <pre className="text-[11px] bg-muted/40 border rounded p-2 whitespace-pre-wrap break-words font-mono">
                  {parsedDescription.shape}
                </pre>
              </div>
            )}
          </div>
        );
      })}
      <details className="mt-2">
        <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground select-none">
          <T>JSON Schema</T>
        </summary>
        <pre className="mt-1.5 text-[11px] bg-muted/40 border rounded p-2 whitespace-pre-wrap break-words font-mono">
          {JSON.stringify(entries, null, 2)}
        </pre>
      </details>
    </>
  );
}

/**
 * Editor for a single array<string> static input.
 * Renders a list of values with add/remove buttons.
 */
function ArrayStringInput({
  socketId,
  value,
  onChange,
  disabled,
}: {
  socketId: string;
  value: string[];
  onChange: (next: string[]) => void;
  disabled: boolean;
}) {
  const gt = useGT();
  const [draft, setDraft] = useState('');

  const addItem = () => {
    const trimmed = draft.trim();
    if (trimmed.length === 0) return;
    onChange([...value, trimmed]);
    setDraft('');
  };

  return (
    <div className="space-y-1.5">
      {value.map((item, i) => (
        <div key={`${socketId}-${i}`} className="flex items-center gap-1">
          <div className="flex-1 rounded-md border bg-muted/30 px-2 py-1 text-xs truncate">
            {item}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-destructive hover:text-destructive shrink-0"
            disabled={disabled}
            onClick={() => onChange(value.filter((_, j) => j !== i))}
          >
            <XIcon className="w-3 h-3" />
          </Button>
        </div>
      ))}
      <div className="flex items-center gap-1">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addItem();
            }
          }}
          placeholder={gt('Add value...')}
          className="text-xs h-7 flex-1"
          disabled={disabled}
        />
        <Button
          variant="outline"
          size="sm"
          className="h-7 w-7 p-0 shrink-0"
          disabled={disabled || draft.trim().length === 0}
          onClick={addItem}
        >
          <PlusIcon className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Editor for a single string/any static input. Renders the field's label row
 * (name + type badge) with a small segmented control aligned to the right that
 * switches between a plain text input and the markdown WYSIWYG editor. The
 * control auto-selects the WYSIWYG editor when the value already looks like
 * markdown. The stored value is always a plain markdown string either way.
 *
 * This component renders its own label row (rather than relying on the shared
 * one in StaticInputsEditor) so the editor toggle can sit inline with the name.
 */
function StringStaticInput({
  socket,
  rawValue,
  onChange,
  disabled,
}: {
  socket: SocketSchemaEntry;
  rawValue: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const gt = useGT();
  // Auto-detect on first render; user can flip thereafter.
  const [shouldUseMarkdownEditor, setShouldUseMarkdownEditor] = useState(() =>
    looksLikeMarkdown(rawValue),
  );

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Label htmlFor={`static-${socket.id}`} className="text-xs">
          {socket.name}
        </Label>
        <Badge variant="outline" className="text-[10px]">
          {socket.type}
        </Badge>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto h-6 w-6 p-0 text-muted-foreground"
              disabled={disabled}
              aria-label={
                shouldUseMarkdownEditor
                  ? gt('Switch to plain text editor')
                  : gt('Switch to markdown editor')
              }
              onClick={() => setShouldUseMarkdownEditor((v) => !v)}
            >
              {/* Icon reflects the current mode, not the action. */}
              {shouldUseMarkdownEditor ? (
                <MarkdownLogoIcon className="h-3.5 w-3.5" />
              ) : (
                <TextboxIcon className="h-3.5 w-3.5" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {shouldUseMarkdownEditor ? (
              <T context="workflow static input editor toggle">
                Switch to plain text
              </T>
            ) : (
              <T context="workflow static input editor toggle">
                Switch to markdown
              </T>
            )}
          </TooltipContent>
        </Tooltip>
      </div>
      {shouldUseMarkdownEditor ? (
        <MarkdownWYSIWYGEditor
          initialValue={rawValue}
          onChange={onChange}
          placeholder={gt('Default value for {name}', { name: socket.name })}
          height={140}
          readonly={disabled}
        />
      ) : (
        <Input
          id={`static-${socket.id}`}
          value={rawValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={gt('Default value for {name}', { name: socket.name })}
          className="text-xs h-8"
          disabled={disabled}
        />
      )}
    </div>
  );
}

/**
 * Editor for setting static/default values on input sockets.
 * Static values are used when a socket is not wired to an upstream step.
 * If a socket IS wired, the wired value takes precedence at runtime.
 *
 * Renders type-appropriate inputs:
 * - string/uuid: text input
 * - number: number input
 * - boolean: toggle switch
 * - array<string>: add/remove list
 * - json/array<json>: textarea
 */
function StaticInputsEditor({
  entries,
  config,
  onConfigChange,
  readOnly,
}: {
  entries: SocketSchemaEntry[];
  config: Record<string, unknown>;
  onConfigChange: (newConfig: Record<string, unknown>) => void;
  readOnly: boolean;
}) {
  const gt = useGT();
  // Filter out flow control sockets
  const editableEntries = entries.filter(
    (e) => e.id !== '__flow_control_in__' && e.id !== '__flow_control_out__',
  );

  if (editableEntries.length === 0) return null;

  const staticInputs =
    (config['__static_inputs'] as Record<string, unknown> | undefined) ?? {};

  const updateStaticInput = (socketId: string, value: unknown) => {
    const next = { ...staticInputs };
    // Remove empty values to keep config clean
    const isEmpty =
      value === '' ||
      value === undefined ||
      (Array.isArray(value) && value.length === 0);
    if (isEmpty) {
      delete next[socketId];
    } else {
      next[socketId] = value;
    }
    onConfigChange({
      ...config,
      __static_inputs: Object.keys(next).length > 0 ? next : undefined,
    });
  };

  const renderInput = (socket: SocketSchemaEntry) => {
    const raw = staticInputs[socket.id];
    const type = socket.type;

    // boolean → toggle
    if (type === 'boolean') {
      return (
        <div className="flex items-center gap-2 h-8">
          <Switch
            id={`static-${socket.id}`}
            checked={raw === true}
            onCheckedChange={(checked) => updateStaticInput(socket.id, checked)}
            disabled={readOnly}
          />
          <Label htmlFor={`static-${socket.id}`} className="text-xs">
            {raw === true ? <T>true</T> : <T>false</T>}
          </Label>
        </div>
      );
    }

    // number → number input
    if (type === 'number') {
      return (
        <Input
          id={`static-${socket.id}`}
          type="number"
          value={typeof raw === 'number' ? String(raw) : ''}
          onChange={(e) => {
            const v = e.target.value;
            updateStaticInput(socket.id, v.length > 0 ? Number(v) : undefined);
          }}
          placeholder={gt('Default value for {name}', { name: socket.name })}
          className="text-xs h-8"
          disabled={readOnly}
        />
      );
    }

    // array<string> / array<uuid> → add/remove list
    if (type === 'array<string>' || type === 'array<uuid>') {
      const items = Array.isArray(raw) ? (raw as string[]) : [];
      return (
        <ArrayStringInput
          socketId={socket.id}
          value={items}
          onChange={(next) => updateStaticInput(socket.id, next)}
          disabled={readOnly}
        />
      );
    }

    // json / array<json> / array<*> → textarea
    if (type === 'json' || type.startsWith('array<')) {
      return (
        <Textarea
          id={`static-${socket.id}`}
          value={
            typeof raw === 'string'
              ? raw
              : raw
                ? JSON.stringify(raw, null, 2)
                : ''
          }
          onChange={(e) => updateStaticInput(socket.id, e.target.value)}
          placeholder={gt('JSON value for {name}', { name: socket.name })}
          className="text-xs font-mono min-h-16 resize-y"
          disabled={readOnly}
        />
      );
    }

    // uuid / date / other → plain text input.
    // (string / any are handled separately in the map below, since they render
    // their own label row with an inline editor-type toggle.)
    return (
      <Input
        id={`static-${socket.id}`}
        value={typeof raw === 'string' ? raw : ''}
        onChange={(e) => updateStaticInput(socket.id, e.target.value)}
        placeholder={gt('Default value for {name}', { name: socket.name })}
        className="text-xs h-8"
        disabled={readOnly}
      />
    );
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        <T context="workflow step input defaults section description">
          Values used when an input isn&apos;t wired on the canvas. Wired
          connections override these at runtime.
        </T>
      </p>
      <div className="space-y-3">
        {editableEntries.map((socket) => {
          // string / any render their own label row (with the inline editor
          // toggle aligned to the field name).
          if (socket.type === 'string' || socket.type === 'any') {
            const raw = staticInputs[socket.id];
            return (
              <StringStaticInput
                key={socket.id}
                socket={socket}
                rawValue={typeof raw === 'string' ? raw : ''}
                onChange={(value) => updateStaticInput(socket.id, value)}
                disabled={readOnly}
              />
            );
          }
          return (
            <div key={socket.id} className="space-y-1">
              <div className="flex items-center gap-2">
                <Label htmlFor={`static-${socket.id}`} className="text-xs">
                  {socket.name}
                </Label>
                <Badge variant="outline" className="text-[10px]">
                  {socket.type}
                </Badge>
              </div>
              {renderInput(socket)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SocketReferenceSection({
  inputEntries,
  outputEntries,
}: {
  inputEntries: SocketSchemaEntry[];
  outputEntries: SocketSchemaEntry[];
}) {
  return (
    <div className="space-y-4 border-t pt-3">
      <div>
        <h4 className="text-xs font-medium text-foreground mb-2">
          <T context="workflow step socket reference inputs heading">Inputs</T>
        </h4>
        <SocketList
          entries={inputEntries}
          keyPrefix="input-ref"
          emptyLabel="No inputs"
          showRequired
        />
      </div>
      <div>
        <h4 className="text-xs font-medium text-foreground mb-2">
          <T context="workflow step socket reference outputs heading">
            Outputs
          </T>
        </h4>
        <SocketList
          entries={outputEntries}
          keyPrefix="output-ref"
          emptyLabel="No outputs"
        />
      </div>
    </div>
  );
}

function parseJsonConfig(
  text: string,
): { value: Record<string, unknown> } | { error: string } {
  try {
    const parsed: unknown = JSON.parse(text);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return { error: 'JSON must be an object' };
    }
    return { value: parsed as Record<string, unknown> };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Invalid JSON' };
  }
}

function JsonConfigSection({
  config,
  onConfigChange,
  readOnly,
}: {
  config: Record<string, unknown>;
  onConfigChange: (newConfig: Record<string, unknown>) => void;
  readOnly: boolean;
}) {
  const gt = useGT();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);
  const [pendingApply, setPendingApply] = useState(false);

  const configJson = JSON.stringify(config, null, 2);
  const hasExistingConfig = Object.keys(config).length > 0;

  const handleDownload = useCallback(() => {
    const blob = new Blob([configJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'step-config.json';
    a.click();
    URL.revokeObjectURL(url);
  }, [configJson]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result;
        if (typeof text === 'string') {
          setImportText(text);
          setImportError(null);
          setPendingApply(false);
        }
      };
      reader.readAsText(file);
      e.target.value = '';
    },
    [],
  );

  const commitApply = useCallback(
    (parsed: Record<string, unknown>) => {
      onConfigChange(parsed);
      setImportText('');
      setImportError(null);
      setPendingApply(false);
      setApplied(true);
      setTimeout(() => setApplied(false), 1500);
    },
    [onConfigChange],
  );

  const handleApplyClick = useCallback(() => {
    const result = parseJsonConfig(importText);
    if ('error' in result) {
      setImportError(result.error);
      return;
    }
    setImportError(null);
    if (hasExistingConfig) {
      setPendingApply(true);
    } else {
      commitApply(result.value);
    }
  }, [importText, hasExistingConfig, commitApply]);

  const handleConfirmApply = useCallback(() => {
    const result = parseJsonConfig(importText);
    if ('error' in result) {
      setImportError(result.error);
      setPendingApply(false);
      return;
    }
    commitApply(result.value);
  }, [importText, commitApply]);

  return (
    <div className="space-y-4">
      {/* Export */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium">
            <T>Current Config</T>
          </Label>
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-xs gap-1"
            onClick={handleDownload}
          >
            <DownloadSimpleIcon className="h-3 w-3" />
            <T>Download</T>
          </Button>
        </div>
        <pre className="text-[11px] bg-muted/40 border rounded p-2 whitespace-pre-wrap break-all font-mono max-h-48 overflow-auto w-full">
          {configJson}
        </pre>
      </div>

      {/* Import */}
      {!readOnly && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium">
              <T>Import Config</T>
            </Label>
            <Button
              variant="outline"
              size="sm"
              className="h-6 text-xs gap-1"
              onClick={() => fileInputRef.current?.click()}
            >
              <FolderOpenIcon className="h-3 w-3" />
              <T>From file</T>
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            <T>
              Paste or load a JSON config object to replace the current
              configuration.
            </T>
          </p>
          <Textarea
            value={importText}
            onChange={(e) => {
              setImportText(e.target.value);
              setImportError(null);
              setPendingApply(false);
            }}
            placeholder={gt('Paste JSON config here...')}
            className="text-xs font-mono w-full h-28 resize-none block"
            style={{ maxWidth: '100%', boxSizing: 'border-box' }}
          />
          {importError && (
            <p className="text-xs text-destructive break-words">
              {importError}
            </p>
          )}
          {pendingApply ? (
            <div className="rounded-md border border-amber-500/40 bg-amber-50/60 dark:bg-amber-950/20 p-2.5 space-y-2">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                <T>
                  This will override the current config. This cannot be undone.
                </T>
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="destructive"
                  className="flex-1 h-7 text-xs gap-1"
                  onClick={handleConfirmApply}
                >
                  <CheckIcon className="h-3.5 w-3.5" />
                  <T>Confirm override</T>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 h-7 text-xs"
                  onClick={() => setPendingApply(false)}
                >
                  <T>Cancel</T>
                </Button>
              </div>
            </div>
          ) : (
            <Button
              size="sm"
              className="w-full gap-1"
              variant="outline"
              disabled={importText.trim().length === 0}
              onClick={handleApplyClick}
            >
              {applied ? (
                <>
                  <CheckIcon className="h-3.5 w-3.5 text-green-600" />
                  <T>Applied</T>
                </>
              ) : (
                <>
                  <UploadSimpleIcon className="h-3.5 w-3.5" />
                  <T>Apply</T>
                </>
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export function WorkflowEditorPanel({
  selectedNode,
  onConfigChange,
  onLabelChange,
  onClose,
  onExpand,
  isMinimized,
  workflowDefinitionId,
  readOnly = false,
  readOnlyVariant,
  viewOnly = false,
  registerStepTool,
}: WorkflowEditorPanelProps) {
  const gt = useGT();
  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const { currentUser: _currentUser } = useContext(AuthContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [nameEditor, setNameEditor] = useState<{
    nodeId: string;
    value: string;
  } | null>(null);
  const [panelWidth, setPanelWidth] = useState(400);
  const isResizing = useRef(false);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isResizing.current = true;
      const startX = e.clientX;
      const startWidth = panelWidth;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const delta = startX - moveEvent.clientX;
        setPanelWidth(Math.max(320, Math.min(800, startWidth + delta)));
      };

      const handleMouseUp = () => {
        isResizing.current = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [panelWidth],
  );

  const nodeData = selectedNode?.data;
  const { showToast } = useToast();
  const configChangeCountRef = useRef(0);

  const selectedNodeId = selectedNode?.id;
  const [activeTab, setActiveTab] = useState('config');
  const [dynamicRefreshFn, setDynamicRefreshFn] = useState<
    (() => Promise<void>) | null
  >(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  useEffect(() => {
    configChangeCountRef.current = 0;
    setActiveTab('config');
    setDynamicRefreshFn(null);
    setIsRefreshing(false);
  }, [selectedNodeId]);

  const registerDynamicRefresh = useCallback(
    (refresh: (() => Promise<void>) | null) => {
      setDynamicRefreshFn(() => refresh);
    },
    [],
  );

  const handleDynamicRefresh = useCallback(async () => {
    if (!dynamicRefreshFn) return;
    setIsRefreshing(true);
    try {
      await dynamicRefreshFn();
    } finally {
      setIsRefreshing(false);
    }
  }, [dynamicRefreshFn]);

  const evalConfig = nodeData?._stepTypeData?.evalConfig;
  const { data: testSuitesData } = useListTestSuites(
    workflowDefinitionId && selectedNode && evalConfig
      ? selectedNode.id
      : undefined,
  );
  const hasTestSuites = (testSuitesData?.testSuites?.length ?? 0) > 0;

  // `listStepTypes` omits config schemas by default; the assistant needs the one
  // for the open step, so fetch it unless the list already carried it. Its only
  // consumer is StepConfigAssistant, which renders under `!readOnly` — so a
  // read-only panel (the overview tab's mode) must not fetch on every node click.
  const inlineConfigSchema = nodeData?._stepTypeData?.configSchema as
    Record<string, unknown> | undefined;
  const { data: fetchedConfigSchema } = useStepTypeConfigSchema(
    !readOnly && !inlineConfigSchema ? nodeData?.stepType : undefined,
  );
  const assistantConfigSchema = inlineConfigSchema ?? fetchedConfigSchema;

  const ConfigRenderer = useMemo(() => {
    if (!nodeData?.stepType) return null;
    return getConfigRenderer(nodeData.stepType);
  }, [nodeData?.stepType]);

  const handleConfigChange = useCallback(
    (newConfig: Record<string, unknown>) => {
      if (!selectedNode?.id) return;

      const result = onConfigChange(selectedNode.id, newConfig);

      if (hasTestSuites) {
        configChangeCountRef.current += 1;
        if (configChangeCountRef.current === 1) {
          showToast({
            title: 'Step config changed',
            message:
              'Switch to the Evals tab to re-run your test suite and check for regressions.',
            color: 'info',
          });
        }
      }

      // Return the resolution (e.g. regenerated sockets) so useDynamicStepState
      // can hand it to the agent.
      return result;
    },
    [selectedNode?.id, onConfigChange, hasTestSuites, showToast],
  );

  const stepType = nodeData?.stepType ?? 'unknown';
  const isTriggerStep = stepType.toLowerCase().endsWith('trigger');
  // Mirror the node renderer's category resolution: direct field → step type metadata → trigger inference
  const stepCategory =
    (nodeData?.category as string | undefined) ??
    nodeData?._stepTypeData?.category ??
    (isTriggerStep ? 'Trigger' : undefined);
  const hasEvals = nodeData?._stepTypeData?.evalConfig != null;
  const stepTypeForTest = nodeData?.stepType;
  const hasGenericTest =
    !!stepTypeForTest && GENERIC_TEST_TAB_STEP_TYPES.has(stepTypeForTest);
  const hasDocTest =
    hasDocumentInputSockets(nodeData?._stepTypeData) || hasGenericTest;
  const showDocTestTab = stepType !== HTTP_TRIGGER_STEP_TYPE && hasDocTest;
  const showEvalsTab =
    hasEvals &&
    (stepType !== HTTP_TRIGGER_STEP_TYPE ||
      httpTriggerBodySchemaPinned(nodeData?.config));
  const hasMultipleTabs = showDocTestTab || showEvalsTab;
  const defaultName = nodeData?._stepTypeData?.name ?? '';

  useEffect(() => {
    if (activeTab === 'test' && !showDocTestTab) setActiveTab('config');
    if (activeTab === 'tests' && !showEvalsTab) setActiveTab('config');
    if (
      activeTab === 'execution' ||
      activeTab === 'inputs' ||
      activeTab === 'outputs'
    ) {
      setActiveTab('config');
    }
  }, [activeTab, showDocTestTab, showEvalsTab]);
  const readableStepType =
    nodeData?._stepTypeData?.name ??
    stepType.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  const panelCategory = selectedNode ? stepCategory : undefined;
  const categoryStyle = useMemo(
    () => getCategoryStyle(panelCategory),
    [panelCategory],
  );
  const CategoryIcon = useMemo(
    () => getCategoryIcon(panelCategory),
    [panelCategory],
  );
  const isEditingName = selectedNode?.id
    ? nameEditor?.nodeId === selectedNode.id
    : false;

  const handleStartNameEdit = useCallback(() => {
    if (!selectedNode || !nodeData) return;
    setNameEditor({
      nodeId: selectedNode.id,
      value: nodeData.label ?? '',
    });
  }, [selectedNode, nodeData]);

  const handleCancelNameEdit = useCallback(() => {
    setNameEditor(null);
  }, []);

  const handleCommitNameEdit = useCallback(() => {
    if (!selectedNode || !isEditingName || !nameEditor) return;
    onLabelChange(selectedNode.id, nameEditor.value);
    setNameEditor(null);
  }, [selectedNode, isEditingName, nameEditor, onLabelChange]);

  const handleResetNameToDefault = useCallback(() => {
    if (!selectedNode || !defaultName) return;
    onLabelChange(selectedNode.id, defaultName);
    setNameEditor(null);
  }, [selectedNode, defaultName, onLabelChange]);

  const [copiedStepJson, setCopiedStepJson] = useState(false);
  const handleCopyStepJson = useCallback(async () => {
    if (!nodeData) return;
    const stepSnapshot = {
      type: nodeData.stepType,
      name: nodeData.label,
      category: nodeData._stepTypeData?.category,
      config: nodeData.config ?? {},
    };
    const json = JSON.stringify(stepSnapshot, null, 2);
    await navigator.clipboard.writeText(json);
    setCopiedStepJson(true);
    setTimeout(() => setCopiedStepJson(false), 1500);
  }, [nodeData]);

  const stepTypeData = nodeData?._stepTypeData as StepTypeMetadata | undefined;

  const inputSchemaEntries = useMemo(() => {
    if (stepTypeData?.inputSockets && stepTypeData.inputSockets.length > 0) {
      return stepTypeData.inputSockets;
    }

    // The record key is the socket id, so the label has to come off the entry —
    // reading it from the key shows every dynamic socket in the panel under its
    // id instead of its name. Key stays as a fallback for older entries.
    return Object.entries(nodeData?.inputs ?? {}).map(
      ([key, socket]): SocketSchemaEntry => ({
        id: socket.id,
        name: socket.name || key,
        type: socket.socketType ?? 'any',
        required: true,
      }),
    );
  }, [stepTypeData?.inputSockets, nodeData?.inputs]);

  const outputSchemaEntries = useMemo(() => {
    const stepType = nodeData?.stepType ?? '';
    const behavior = DYNAMIC_SOCKET_BEHAVIORS[stepType];
    const liveOutputs = nodeData?.outputs ?? {};
    // Palette metadata only lists base sockets (e.g. identify_customer → top_pick).
    // Prefer handles actually on the node so the panel matches the canvas and DB.
    if (behavior?.dynamicOutputs && Object.keys(liveOutputs).length > 0) {
      return Object.entries(liveOutputs).map(
        ([key, socket]): SocketSchemaEntry => ({
          id: socket.id,
          name: socket.name || key,
          type: socket.socketType ?? 'any',
        }),
      );
    }

    if (stepTypeData?.outputSockets && stepTypeData.outputSockets.length > 0) {
      return stepTypeData.outputSockets;
    }

    return Object.entries(liveOutputs).map(
      ([key, socket]): SocketSchemaEntry => ({
        id: socket.id,
        name: socket.name || key,
        type: socket.socketType ?? 'any',
      }),
    );
  }, [nodeData?.stepType, nodeData?.outputs, stepTypeData?.outputSockets]);

  const stepConfig = (nodeData?.config ?? {}) as Record<string, unknown>;
  const staticInputCount = countStaticInputs(stepConfig);
  const hasStaticInputDefaults = staticInputCount > 0;
  const { sections, setSectionOpen } = useWorkflowPanelSections(
    hasStaticInputDefaults,
  );
  const hasExecutionConfig = hasExecutionOverrides(stepConfig);
  const guardrailAssignments = useMemo(
    () => getWorkflowGuardrailAssignmentConfigs(nodeData?.config),
    [nodeData?.config],
  );
  const guardrailCount = guardrailAssignments.length;
  const showGuardrailsSection =
    GUARDED_STEP_TYPES.has(stepType) || guardrailCount > 0;
  const hasBatchConfig =
    typeof stepConfig.batch === 'object' && stepConfig.batch !== null;
  const showAdvancedBadge = hasExecutionConfig || hasBatchConfig;
  const showInputDefaultsSection = inputSchemaEntries.length > 0;

  const configPanelWrapperClassName =
    'min-h-0 min-w-0 w-full max-w-full flex-1 overflow-x-hidden overflow-y-hidden flex flex-col';

  // `form_trigger` keeps Copy / Open URL actions interactive on finalized
  // (read-only) versions. A disabled <fieldset> would grey out and block those
  // buttons; the renderer already disables editable controls via `readOnly`.
  const wrapConfigInDisabledFieldset = stepType !== FORM_TRIGGER_STEP_TYPE;

  // Renderers come out of the registry as lazy components (one chunk each), so
  // the panel needs its own boundary while the selected step's chunk arrives.
  const configRenderer =
    ConfigRenderer && selectedNode && nodeData ? (
      <Suspense
        fallback={
          <div className="space-y-2">
            <Skeleton className="h-8" />
            <Skeleton className="h-8" />
            <Skeleton className="h-24" />
          </div>
        }
      >
        <ConfigRenderer
          key={selectedNode.id}
          node={selectedNode}
          config={nodeData.config ?? {}}
          onConfigChange={handleConfigChange}
          readOnly={readOnly}
          registerDynamicRefresh={registerDynamicRefresh}
          registerStepTool={readOnly ? undefined : registerStepTool}
        />
      </Suspense>
    ) : null;

  const configPanelContent =
    selectedNode && nodeData ? (
      <>
        <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto">
          {wrapConfigInDisabledFieldset ? (
            <fieldset
              disabled={readOnly}
              className="min-w-0 w-full max-w-full p-3 disabled:opacity-60"
            >
              {configRenderer}
            </fieldset>
          ) : (
            <div className="min-w-0 w-full max-w-full p-3">
              {configRenderer}
            </div>
          )}
        </div>
        <div className="flex-shrink-0 border-t bg-background">
          {showInputDefaultsSection && (
            <PanelSectionCollapsible
              title={
                <T context="workflow step panel collapsible input defaults">
                  Defaults
                </T>
              }
              open={sections.defaults}
              onOpenChange={(open) => setSectionOpen('defaults', open)}
              badge={
                staticInputCount > 0 ? (
                  <PanelSectionBadge>{staticInputCount}</PanelSectionBadge>
                ) : undefined
              }
            >
              <StaticInputsEditor
                key={selectedNode.id}
                entries={inputSchemaEntries}
                config={stepConfig}
                onConfigChange={handleConfigChange}
                readOnly={readOnly}
              />
            </PanelSectionCollapsible>
          )}
          {showGuardrailsSection && (
            <PanelSectionCollapsible
              title={
                <T context="workflow step panel collapsible guardrails">
                  Guardrails
                </T>
              }
              open={sections.guardrails}
              onOpenChange={(open) => setSectionOpen('guardrails', open)}
              badge={
                guardrailCount > 0 ? (
                  <PanelSectionBadge>{guardrailCount}</PanelSectionBadge>
                ) : undefined
              }
            >
              <fieldset disabled={readOnly} className="disabled:opacity-60">
                <GuardrailPolicyAssignments
                  embedded
                  config={nodeData.config ?? {}}
                  onConfigChange={handleConfigChange}
                  readOnly={readOnly}
                />
              </fieldset>
            </PanelSectionCollapsible>
          )}
          <PanelSectionCollapsible
            title={
              <T context="workflow step panel collapsible advanced options">
                Advanced
              </T>
            }
            open={sections.advanced}
            onOpenChange={(open) => setSectionOpen('advanced', open)}
            badge={
              showAdvancedBadge ? (
                <PanelSectionBadge variant="outline">
                  <T context="workflow step panel advanced section has overrides">
                    Set
                  </T>
                </PanelSectionBadge>
              ) : undefined
            }
          >
            <fieldset
              disabled={readOnly}
              className="space-y-4 disabled:opacity-60"
            >
              <ExecutionConfigSection
                key={selectedNode.id}
                config={stepConfig}
                onConfigChange={handleConfigChange}
                readOnly={readOnly}
              />
              {/*
                Fanout batching lives beside the other execution overrides
                rather than in each step's own renderer: it is engine behaviour
                (`WorkflowStepBatchConfigSchema`), identical for every step that
                fans out, and it is tuning rather than authoring.
              */}
              <BatchOptionsFields
                key={`batch-${selectedNode.id}`}
                node={selectedNode}
                config={stepConfig}
                onConfigChange={handleConfigChange}
                readOnly={readOnly}
              />
              <SocketReferenceSection
                inputEntries={inputSchemaEntries}
                outputEntries={outputSchemaEntries}
              />
            </fieldset>
          </PanelSectionCollapsible>
          {!readOnly && nodeData.stepType && (
            <PanelSectionCollapsible
              title={
                <T context="workflow step panel collapsible AI assistant">
                  AI Assistant
                </T>
              }
              open={sections.assistant}
              onOpenChange={(open) => setSectionOpen('assistant', open)}
            >
              <StepConfigAssistant
                embedded
                stepType={nodeData.stepType}
                config={stepConfig}
                configSchema={assistantConfigSchema}
                stepDescription={nodeData._stepTypeData?.description}
                onConfigChange={handleConfigChange}
              />
            </PanelSectionCollapsible>
          )}
          <PanelSectionCollapsible
            title={
              <T context="workflow step panel collapsible JSON export/import">
                Config JSON
              </T>
            }
            open={sections.json}
            onOpenChange={(open) => setSectionOpen('json', open)}
            contentClassName="max-h-[500px]"
          >
            <JsonConfigSection
              config={stepConfig}
              onConfigChange={handleConfigChange}
              readOnly={readOnly}
            />
          </PanelSectionCollapsible>
        </div>
      </>
    ) : null;

  if (isMinimized && !selectedNode) {
    if (viewOnly) {
      return null;
    }
    return (
      <button
        type="button"
        onClick={onExpand}
        className="flex h-full w-10 flex-shrink-0 flex-col items-center justify-center gap-2 border-l bg-background hover:bg-muted transition-colors cursor-pointer"
        title={gt('Open Step Library')}
      >
        <HammerIcon className="w-4 h-4 text-muted-foreground" />
        <span className="text-[11px] font-medium text-muted-foreground [writing-mode:vertical-lr] rotate-180 select-none">
          <T>Step Library</T>
        </span>
      </button>
    );
  }

  return (
    <div
      className={cn(
        'bg-background flex h-full min-w-0 max-w-full flex-shrink-0 overflow-hidden',
        selectedNode && categoryStyle.selectedBorder,
      )}
      style={{ width: panelWidth, maxWidth: panelWidth }}
    >
      <div
        className="w-1 cursor-col-resize hover:bg-primary/20 active:bg-primary/30 flex-shrink-0 border-l"
        onMouseDown={handleResizeStart}
      />
      <div className="flex min-h-0 min-w-0 w-0 flex-1 flex-col overflow-hidden">
        <div
          className={cn(
            'flex items-center justify-between p-1.5 border-b flex-shrink-0',
            selectedNode && categoryStyle.header,
          )}
        >
          <div className="flex items-center gap-2 min-w-0">
            {selectedNode && nodeData ? (
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <CategoryIcon className="w-4 h-4 flex-shrink-0 opacity-80" />
                {isEditingName && !readOnly ? (
                  <div className="flex items-center gap-1 min-w-0 flex-1">
                    <Input
                      value={nameEditor?.value ?? ''}
                      onChange={(e) =>
                        setNameEditor((prev) =>
                          prev
                            ? {
                                ...prev,
                                value: e.target.value,
                              }
                            : prev,
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleCommitNameEdit();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          handleCancelNameEdit();
                        }
                      }}
                      onBlur={handleCommitNameEdit}
                      className="h-7 text-sm"
                      autoFocus
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 flex-shrink-0"
                      onMouseDown={(e) => {
                        e.preventDefault();
                      }}
                      onClick={handleCommitNameEdit}
                    >
                      <CheckIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 flex-shrink-0"
                      onMouseDown={(e) => {
                        e.preventDefault();
                      }}
                      onClick={handleCancelNameEdit}
                    >
                      <XIcon className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 min-w-0 flex-1">
                    <span
                      className="text-sm font-medium truncate cursor-text"
                      onDoubleClick={readOnly ? undefined : handleStartNameEdit}
                    >
                      {nodeData.label}
                    </span>
                    {!readOnly && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 flex-shrink-0"
                        onClick={handleStartNameEdit}
                      >
                        <PencilSimpleIcon className="h-4 w-4" />
                      </Button>
                    )}
                    {!readOnly &&
                      defaultName &&
                      nodeData.label !== defaultName && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                          onClick={handleResetNameToDefault}
                        >
                          <T>Reset</T>
                        </Button>
                      )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <HammerIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm font-medium">
                  <T>Step Library</T>
                </span>
              </div>
            )}
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 flex-shrink-0"
            onClick={onClose}
            title={gt('Close')}
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </div>

        {selectedNode && nodeData ? (
          <>
            <div className="p-3 border-b flex-shrink-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={cn('text-xs border', categoryStyle.badge)}>
                  {stepCategory ?? <T>Utility</T>}
                </Badge>
                <span className="text-xs text-muted-foreground flex-1">
                  {readableStepType}
                </span>
                {dynamicRefreshFn && !readOnly && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 flex-shrink-0"
                        onClick={handleDynamicRefresh}
                        disabled={isRefreshing}
                        aria-label={gt('Refresh dynamic data')}
                        title={gt('Refresh dynamic data')}
                      >
                        <ArrowsClockwiseIcon
                          className={cn(
                            'h-3.5 w-3.5',
                            isRefreshing && 'animate-spin',
                          )}
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <T>Refresh dynamic data</T>
                    </TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 flex-shrink-0"
                      onClick={handleCopyStepJson}
                    >
                      {copiedStepJson ? (
                        <CheckIcon className="h-3.5 w-3.5 text-green-600" />
                      ) : (
                        <CopyIcon className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <T>Copy step config as JSON</T>
                  </TooltipContent>
                </Tooltip>
              </div>
              {nodeData._stepTypeData?.description && (
                <p className="text-xs text-muted-foreground mt-2">
                  {nodeData._stepTypeData.description}
                </p>
              )}
            </div>

            <Separator />

            {hasMultipleTabs ? (
              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="flex min-h-0 min-w-0 w-full max-w-full flex-1 flex-col overflow-hidden"
              >
                <div className="px-3 pt-3">
                  <TabsList
                    className={`grid w-full ${getTabGridCols(showEvalsTab, showDocTestTab)}`}
                  >
                    <TabsTrigger value="config">
                      <T>Config</T>
                    </TabsTrigger>
                    {showDocTestTab && (
                      <TabsTrigger value="test">
                        <T>Test</T>
                      </TabsTrigger>
                    )}
                    {showEvalsTab && (
                      <TabsTrigger
                        value="tests"
                        // The evals tab is the panel's heaviest chunk; start it
                        // on hover so the tab switch doesn't show a skeleton.
                        onMouseEnter={() => warmChunk(loadTestSuiteTab)}
                        onFocus={() => warmChunk(loadTestSuiteTab)}
                      >
                        <T>Evals</T>
                      </TabsTrigger>
                    )}
                  </TabsList>
                </div>

                <TabsContent
                  value="config"
                  className={cn(
                    configPanelWrapperClassName,
                    'mt-0 not-data-[hidden]:flex not-data-[hidden]:flex-col',
                  )}
                >
                  {configPanelContent}
                </TabsContent>

                {showDocTestTab && selectedNode && nodeData?.stepType && (
                  <TabsContent
                    value="test"
                    className="flex-1 min-h-0 mt-0 overflow-hidden not-data-[hidden]:flex not-data-[hidden]:flex-col"
                  >
                    <ScrollArea className="h-full" type="auto">
                      {hasGenericTest ? (
                        <GenericStepTestSection
                          stepType={nodeData.stepType}
                          config={nodeData.config ?? {}}
                          inputSockets={
                            nodeData._stepTypeData?.inputSockets ?? []
                          }
                        />
                      ) : (
                        <StepTestSection
                          stepType={nodeData.stepType}
                          config={nodeData.config ?? {}}
                          inputSockets={
                            nodeData._stepTypeData?.inputSockets ?? []
                          }
                        />
                      )}
                    </ScrollArea>
                  </TabsContent>
                )}

                {showEvalsTab && (
                  <TabsContent
                    value="tests"
                    className="flex-1 min-h-0 mt-0 overflow-hidden not-data-[hidden]:flex not-data-[hidden]:flex-col"
                  >
                    {workflowDefinitionId && selectedNode && (
                      <Suspense
                        fallback={<Skeleton className="flex-1 rounded-none" />}
                      >
                        <TestSuiteTab
                          workflowDefinitionId={workflowDefinitionId}
                          workflowStepId={selectedNode.id}
                          evalConfig={nodeData?._stepTypeData?.evalConfig}
                          stepConfig={nodeData?.config}
                        />
                      </Suspense>
                    )}
                  </TabsContent>
                )}
              </Tabs>
            ) : (
              <div
                className={cn(
                  configPanelWrapperClassName,
                  'flex min-h-0 min-w-0 w-full max-w-full flex-1 overflow-hidden',
                )}
              >
                {configPanelContent}
              </div>
            )}
          </>
        ) : readOnly ? (
          <div className="flex-1 min-h-0 overflow-hidden p-4 text-sm text-muted-foreground">
            <ReadOnlyPanelPlaceholder
              variant={readOnlyVariant}
              viewOnly={viewOnly}
            />
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-hidden">
            <NodeLibraryContent
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
          </div>
        )}
      </div>
    </div>
  );
}
